import type { ReactNode } from "react";

/** Page header with title + optional subtitle/actions, matching the Vault chrome. */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-ink-100 px-4 py-4 sm:px-6 sm:py-5">
      <div>
        <h1 className="font-headline text-[20px] leading-tight text-ink-900 sm:text-[22px]">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[13px] text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** A single KPI tile. */
export function StatCard({ label, value, hint, tone = "ink" }: { label: string; value: ReactNode; hint?: string; tone?: "ink" | "gold" | "emerald" | "rose" }) {
  const valueCls =
    tone === "gold" ? "text-gold-700" : tone === "emerald" ? "text-emerald" : tone === "rose" ? "text-rose" : "text-ink-900";
  return (
    <div className="surface-card p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">{label}</p>
      <p className={`mt-1.5 text-[26px] font-semibold leading-none tabular ${valueCls}`}>{value}</p>
      {hint && <p className="mt-1.5 text-[11.5px] text-ink-400">{hint}</p>}
    </div>
  );
}

export const money = (n: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: n % 1 === 0 ? 0 : 2 }).format(n);

export const shortDate = (d?: string | number | null) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_CLS: Record<string, string> = {
  approved: "bg-emerald/10 text-emerald",
  active: "bg-emerald/10 text-emerald",
  paid: "bg-emerald/10 text-emerald",
  pending: "bg-gold-50 text-gold-700",
  open: "bg-gold-50 text-gold-700",
  draft: "bg-ink-100 text-ink-500",
  archived: "bg-ink-100 text-ink-500",
  banned: "bg-rose/10 text-rose",
  void: "bg-rose/10 text-rose",
  uncollectible: "bg-rose/10 text-rose",
  manual: "bg-gold-50 text-gold-700",
  stripe: "bg-ink-100 text-ink-600",
};

export function StatusPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium capitalize ${STATUS_CLS[status] ?? "bg-ink-100 text-ink-500"}`}>
      {status}
    </span>
  );
}
